import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import PillInput from '../../../UISystem/Forms/PillInput';
import ContactTypeInput from '../../../UISystem/Forms/ContactTypeInput';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { SHADOWS } from '../../../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../../../utils/contactTypeConfig';

// What the selection sheet offers (user rules, device pass 1j): the trio is
// ALWAYS there; the two-way channels join only when the Profile shared
// theirs (reciprocity — never ask for a channel the host didn't offer).
// Content-broadcast types (Substack, YouTube, TikTok, Twitter, Twitch) and
// the rest never appear — this is how people text each other, not a follow.
const ALWAYS_TYPES = ['phone', 'instagram', 'email'];
const IF_SHARED_TYPES = ['whatsapp', 'linkedin', 'telegram', 'signal', 'snapchat'];

/**
 * AnonTextShareSheet — the TEXT variant's share bottom sheet (variants block
 * plan 2, epic §8; channel selection added at device pass 1j, NAME FIRST at
 * 1k — number-first felt off): [Share number] goes straight to the name
 * step then the phone field; the caret raises the SELECTION step — Phone
 * leads as the primary pill, the rest of the allowed channels below —
 * then name, then the chosen channel's field. Backdrop tap dismisses at
 * every step, nothing written.
 *
 * V1 stays MANUAL-NEXT (Simplicity pass 2 ruling): no autofill burst
 * detector — the iOS suggestion fills the field, the person taps Next.
 *
 * Sheet chrome rides the QuickAddOverlay/ShareLocationSheet Framer-Motion
 * pattern (ui-design-system §4.2 standard variant — user-initiated, so it
 * takes the scrim).
 *
 * The parent owns persistence: onComplete({ contactType, contactValue,
 * name }) fires ONCE at the final step's Connect — a single submit, a
 * single authoritative contact write upstream (the deterministic-ID race
 * rule). "Connect" rides the LAST step, wherever it lands.
 */
const AnonTextShareSheet = ({ profileData, initialStep = 'select', onComplete, onClose }) => {
  // initialStep 'name' = the [Share number] fast path (name → phone,
  // "Share number becomes Phone"); the caret opens 'select'
  const [step, setStep] = useState(initialStep);
  const [contactType, setContactType] = useState('phone');
  const [contactValue, setContactValue] = useState('');
  const [name, setName] = useState('');
  const nameInputRef = useRef(null);

  // Body scroll lock (match QuickAddOverlay)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus the name field when its step arrives (the contact field
  // autofocuses itself via ContactTypeInput)
  useEffect(() => {
    if (step === 'name') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step]);

  // Reciprocity gate — same predicate as the card's channel rows (value
  // alone is never proof of consent)
  const sharedFields = profileData?.sharedFields || [];
  const optionTypes = [
    ...ALWAYS_TYPES,
    ...IF_SHARED_TYPES.filter((t) => profileData?.[t] && sharedFields.includes(t)),
  ];

  const typeConfig = ALL_CONTACT_TYPES[contactType];
  const trimmedValue = contactValue.trim();
  const trimmedName = name.trim();
  const canNext = step === 'value' ? !!trimmedValue : trimmedName.length > 0;

  const handleSelect = (typeId) => {
    setContactType(typeId);
    setContactValue('');
    setStep('name');
  };

  const handleNext = (e) => {
    if (!canNext) return;
    if (step === 'name') {
      setStep('value');
      return;
    }
    // The Connect tap's viewport point — the parent's star burst emanates
    // from it (the tap is what makes it happen). Enter-key submits carry
    // no point; the burst falls back to its default origin.
    const tapPoint =
      e && typeof e.clientX === 'number' && e.clientX > 0 ? { x: e.clientX, y: e.clientY } : null;
    onComplete({ contactType, contactValue: trimmedValue, name: trimmedName, tapPoint });
  };

  const renderSelect = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
        maxWidth: '260px',
      }}
    >
      {optionTypes.map((typeId, i) => {
        const config = ALL_CONTACT_TYPES[typeId];
        const Icon = config.Icon;
        const primary = i === 0; // Phone leads, black (mock)
        return (
          <div
            key={typeId}
            onClick={() => handleSelect(typeId)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '44px',
              borderRadius: '22px',
              backgroundColor: primary ? '#000000' : '#FFFFFF',
              border: primary ? '1px solid #000000' : '1px solid #E3E3E7',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {Icon && (
              <Icon
                size={18}
                color={primary ? '#FFFFFF' : '#8E8E93'}
                style={{ position: 'absolute', left: '18px' }}
              />
            )}
            <span
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: primary ? '#FFFFFF' : '#000000',
              }}
            >
              {config.label}
            </span>
          </div>
        );
      })}
    </div>
  );

  const title =
    step === 'select'
      ? null
      : step === 'name'
        ? 'Your name'
        : contactType === 'phone'
          ? 'Your number'
          : `Your ${typeConfig?.label}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
            maxWidth: '340px',
            margin: '0 auto',
          }}
        >
          {title && (
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>{title}</div>
          )}

          {step === 'select' ? (
            renderSelect()
          ) : (
            <div style={{ width: '100%' }}>
              {step === 'value' ? (
                <ContactTypeInput
                  typeConfig={typeConfig}
                  value={contactValue}
                  onChange={setContactValue}
                  onEnter={handleNext}
                />
              ) : (
                <PillInput
                  ref={nameInputRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleNext();
                  }}
                  placeholder="Name"
                  autoComplete="name"
                  autoCapitalize="words"
                />
              )}
            </div>
          )}

          {/* "Connect" is the real commitment moment (user ruling 2026-08-13
              — the intro button became "{name}'s info" so this word lands
              where the connection actually happens: the final submit) */}
          {step !== 'select' && (
            <ButtonRounded
              text={step === 'name' ? 'Next' : 'Connect'}
              variant="primary"
              size="large"
              disabled={!canNext}
              onClick={handleNext}
            />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

AnonTextShareSheet.propTypes = {
  profileData: PropTypes.object,
  initialStep: PropTypes.oneOf(['select', 'name']),
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AnonTextShareSheet;
